import { ApolloError } from '@apollo/client/errors';
import _get from 'lodash/get';
import {
  User,
  UserFilterInputObject,
  UsersQueryVariables,
  useUsersIdsLazyQuery,
  useUsersQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import { ListQueryHookProps } from '../types';

export type UseUsersQueryHookResult = {
  collection: User[];
  error?: ApolloError;
  fetchIds: () => Promise<FetchIdsResponse<unknown>>;
  firstLoading: boolean;
  loading: boolean;
  refetch: () => void;
  totalCount: number;
  totalPages: number;
};

// This is a duplicate of the useUsersQueryHook function with a different fetchPolicy to prevent unnecessary rendering of components that use the original hook
export default function useInterventionAssigneesQueryHook({
  searchQuery,
  paginationParams,
  additionalFilters,
  skip,
}: ListQueryHookProps): UseUsersQueryHookResult {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const usersQueryVariables: UsersQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
  };
  const filters: UserFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'email' },
        { fieldName: 'role', predicateType: FilterSchemaPredicateType.Eq },
        { fieldName: 'fullName' },
        { fieldName: 'personalId' },
        { fieldName: 'memberAssignedSitesName' },
      ]),
    );
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    usersQueryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    usersQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const usersQuery = useUsersQuery({
    fetchPolicy: 'no-cache',
    variables: usersQueryVariables,
    skip,
  });
  const [fetchIds] = useUsersIdsLazyQuery({
    fetchPolicy: 'no-cache',
    variables: {
      onlyIds: true,
      ...usersQueryVariables,
    },
  });
  const { loading, data, previousData, refetch, error } = usersQuery;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(queryData, 'data.collection', []) as User[];
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);
  return {
    collection,
    refetch,
    totalCount,
    totalPages,
    error,
    firstLoading,
    loading,
    fetchIds,
  };
}
